<template>
  <div
    id="spa-top"
    class="content-group -column -center -vs-small -hs-small -no-spacer member-page-color-10"
  >
    <!-- header -->
    <section class="group -flex -wide -stack-space-single -relative">
      <profile-header :beta-pill="true" :edit-profile="true" :view-dashboard="true" />
    </section>

    <!-- rewards -->
    <reward-widget />

    <!-- spa scroll point -->
    <section
      id="referral-card-top"
      ref="referralCardTop"
      tabindex="-1"
      class="group -flex -wide -stack-space-single"
    >
      <div class="referral-card">
        <div class="card-header">
          <h1 id="spa-title" class="heading -secondary">Referrals</h1>

          <!-- closer for referral code screen -->
          <div v-if="isCodeReady && screen === 'referral-code'" class="card-action">
            <icon-button
              icon="close"
              classes="-no-border -ico-only"
              label="Close"
              @click-action="showScreen('referral-conversions')"
            />
          </div>

          <!-- opens referral code screen -->
          <icon-button
            v-if="isCodeReady && screen === 'referral-conversions'"
            type="button"
            icon="account_circle"
            label="Refer a Friend"
            classes="-main -ico-txt -no-case"
            :disabled="isCodeDisabled"
            @click-action="showScreen('referral-code')"
          />
        </div>

        <!-- error -->
        <section v-if="referrals.error" class="">
          <callout type="error" @close-action="clearError">
            <div>{{ referrals.error }}</div>
          </callout>
        </section>

        <!-- disabled code message -->
        <section v-if="isCodeReady && isCodeDisabled" class="">
          <callout type="error" :closable="false">
            <div>Referral code is disabled, please contact support</div>
          </callout>
        </section>

        <!-- no code, opt in screen -->
        <div v-if="screen === 'no-code'" class="card-messages">
          <div class="message">
            Join the referral program to earn rewards for sharing Fitness Blender with family,
            friends and colleagues.
          </div>

          <div v-if="referralRewards" class="message">
            Earn {{ referralRewards.pctPlusPass }}% from purchased plus passes or new auto-renew
            membership signups. See <a href="/referrals-rewards#referral-faqs">Referral FAQs</a> for
            more information.
          </div>

          <div class="message -small">
            By generating your referral link, you agree to the
            <a class="link" href="/page/terms-of-use-agreement#tos12a">Terms of Use</a>.
          </div>
          <div class="cta">
            <submit-button
              class="-dark -no-case"
              type="button"
              :submitting="generateCodeLoading"
              label="Generate Referral Link"
              submitting-label="Processing..."
              @submit-action="generateCode"
            >
            </submit-button>
          </div>
        </div>

        <!-- share referral code screen -->
        <div v-if="isCodeReady && screen === 'referral-code'" class="card-messages">
          <div class="message -small">
            Share your referral link to give your friends a discount and earn wallet bucks for
            yourself.
          </div>
          <div class="referral-link">
            <div class="qr-code">
              <img
                :src="referrals.data!.code.imageUrl"
                alt="QR Code"
                loading="lazy"
                width="150"
                height="150"
              />
            </div>
            <div class="">
              <a class="link" :href="referrals.data!.code.url">{{ referrals.data!.code.url }}</a>
            </div>
          </div>
        </div>

        <!-- referral pagination screen -->
        <div v-if="isCodeReady && screen === 'referral-conversions'">
          <div class="referral-info">
            <div class="referral-count">
              <div class="referral-total">
                {{ referrals.data!.conversions.totalItems }} Referrals in

                <select-input
                  v-model="selectedYear"
                  input-id="year"
                  label="Select Year"
                  :show-label="false"
                  :options="years"
                  @change="pushRouteUpdate({ year: selectedYear })"
                />
              </div>
            </div>
            <div class="referral-reward">
              <div class="referral-status">
                <div class="reward-title">{{ referrals.data!.yearSelected }} Rewards</div>
                <div class="reward-value">
                  ${{ referrals.data!.yearlyRewardAmount }} of ${{
                    referrals.data!.maxRewardAmount
                  }}
                  Wallet Bucks
                </div>
              </div>
              <div class="reward-rules">
                <i class="material-symbols-outlined fill">info</i>
              </div>
            </div>
          </div>

          <!-- skeleton loader -->
          <div v-if="!isListReady" class="referral-list">
            <skeleton-referral v-for="loaderCount in 20" :key="`skeleton-${loaderCount}`" />
          </div>
          <div
            v-else-if="isListReady && referrals.data!.conversions.totalItems > 0"
            class="referral-list"
          >
            <referral
              v-for="conversion in referrals.data!.conversions.pageData"
              :key="conversion.id"
              :conversion="conversion"
            />
            <div class="card-pagination">
              <pagination
                :current-page="referrals.data!.conversions.currentPage"
                :total-pages="referrals.data!.conversions.totalPages"
                :total-items="referrals.data!.conversions.totalItems"
                :per-page="referrals.data!.conversions.perPage"
                @page-update="pageUpdate"
              />
            </div>
          </div>
          <div v-else class="card-messages">
            <div class="message -small">No referrals in {{ referrals.data!.yearSelected }}</div>
          </div>
        </div>
        <!-- end pagination screen -->
      </div>
      <!-- end referral card -->
    </section>
  </div>
</template>

<script setup lang="ts">
/**
 * Referrals page
 */
import { shallowRef, computed, watch, onBeforeMount, ref, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { fbApi, objToSearchParams } from '@/utils/http-api'
import type { ReferralResponseData, ReferralRewards } from '@/types/referral-types'
import Callout from '@/components/notifications/callout.vue'
import SubmitButton from '@/components/buttons/submit-button.vue'
import IconButton from '@/components/buttons/icon-button.vue'
import ProfileHeader from '@/components/users/profile/profile-header.vue'
import Referral from '@/components/users/referrals/referral.vue'
import RewardWidget from '@/components/users/rewards/reward-widget.vue'
import Pagination from '@/components/search/pagination.vue'
import SkeletonReferral from '@/components/loaders/skeleton-referral.vue'
import SelectInput from '@/components/forms/select-input.vue'

/** TYPES **/
type Screen = 'no-code' | 'referral-code' | 'referral-conversions'
type SearchParams = { year?: string; page?: string }
type YearOption = { value: string; label: string }

/** PROPS **/
// none, SPA entry point

/** STATE **/
const route = useRoute()
const router = useRouter()
const screen = ref<Screen>('referral-conversions')
const referralRewards = ref<ReferralRewards | null>(null)
const generateCodeLoading = ref(false)
const referralCardTop = ref<HTMLElement | null>(null)
const years = ref<YearOption[]>([])
const selectedYear = ref('')
const referrals = shallowRef<{
  loading: boolean
  error: string
  data: ReferralResponseData | null
}>({
  loading: false,
  error: '',
  data: null,
})

/** NON REACTIVE **/

/** WATCHERS **/
// search again on route query param change
watch(
  () => route.query,
  (newQueryParams, oldQueryParams) => {
    fetchReferrals({ ...newQueryParams })
  },
)

/** COMPUTED **/
// helper if XHR code actually exists, post fetch and separate from conversion list
const isCodeReady = computed(() => {
  return !!referrals.value.data?.code
})

// helper if conversion list is loaded, post fetch
const isListReady = computed(() => {
  return (
    referrals.value.data &&
    referrals.value.data.code &&
    Array.isArray(referrals.value.data.conversions.pageData) &&
    !referrals.value.error &&
    !referrals.value.loading
  )
})

const isCodeDisabled = computed(() => {
  if (!isListReady.value) {
    return false
  }

  return referrals.value.data!.code.status !== 1
})

/** LIFECYCLE **/
onBeforeMount(async () => {
  await fetchReferrals({ ...route.query })
})

/** METHODS **/
function clearError() {
  referrals.value = {
    ...referrals.value,
    error: '',
  }
}

function pageUpdate(page: number) {
  // force string page number
  let paramsWithPagination = { ...route.query }
  paramsWithPagination.page = `${page}`

  // push router update (which will re-fetch)
  pushRouteUpdate(paramsWithPagination)
}

// primary way to update and get new results
function pushRouteUpdate(params: SearchParams) {
  router.push({ query: params }).catch(() => {})
}

// powers the type of referral card screen that is shown
function showScreen(newScreen: Screen) {
  screen.value = newScreen

  // focus for accessibility
  nextTick(() => {
    if (newScreen === 'referral-code' && referralCardTop.value) {
      referralCardTop.value.focus()
    }
  })
}

// rewards amounts, no loading or error stats... used for no code screen
async function fetchReferralRewards() {
  try {
    const res = await fbApi.get('/my/referrals/rewards')

    referralRewards.value = res.body.data
  } catch (e: any) {
    console.error(e)
  }
}

// fetch referral data
async function fetchReferrals(params: SearchParams) {
  // disable double fetch
  if (referrals.value.loading) {
    return
  }

  // reset conversions
  referrals.value = {
    ...referrals.value,
    loading: true,
    error: '',
  }

  try {
    const res = await fbApi.get('/my/referrals', {
      searchParams: objToSearchParams(params),
    })

    // missing data, shouldn't happen
    if (!res || !res.body) {
      throw new Error(`Missing response data`)
    }

    // code is missing, force no code screen so user can opt in first
    if (!res.body.data) {
      await fetchReferralRewards()
      showScreen('no-code')

      referrals.value = {
        ...referrals.value,
        loading: false,
      }

      return
    }

    // set year data
    setYearsRange(res.body.data.yearStart, res.body.data.yearEnd)
    selectedYear.value = res.body.data.yearSelected

    // full response
    referrals.value = {
      loading: false,
      error: '',
      data: res.body.data,
    }

    // set code and top level items

    showScreen('referral-conversions')
  } catch (e: any) {
    console.error(e)

    referrals.value = {
      ...referrals.value,
      loading: false,
      error: 'Unable to load referrals, please refresh the page and try again',
    }
  }
}

// generate referral code
async function generateCode() {
  // disable multiple clicks
  if (generateCodeLoading.value) {
    return
  }

  generateCodeLoading.value = true

  try {
    await fbApi.post('/my/consents/referrals')

    // re-fetch referral data without loading state
    const res = await fbApi.get('/my/referrals')

    // set year data
    setYearsRange(res.body.data.yearStart, res.body.data.yearEnd)
    selectedYear.value = res.body.data.yearSelected

    // full response
    referrals.value = {
      loading: false,
      error: '',
      data: res.body.data,
    }

    // force show code screen
    generateCodeLoading.value = false
    screen.value = 'referral-code'
  } catch (e: any) {
    console.error(e)

    generateCodeLoading.value = false

    referrals.value = {
      ...referrals.value,
      loading: false,
      error: 'Unable to generate referral code, please refresh the page and try again',
    }
  }
}

// internal method for drop down options
function setYearsRange(yearStart: string, yearEnd: string) {
  let yearsRange = []

  for (let i = parseInt(yearStart); i <= parseInt(yearEnd); i++) {
    yearsRange.push({
      value: i.toString(),
      label: i.toString(),
    })
  }

  years.value = yearsRange
}
</script>
